import React from 'react';
import { Check, X } from 'lucide-react';
import { StatusPill } from '../ui';
import {
  formatNotificationTime,
  formatNotificationTimestamp,
  getNotificationIcon,
  getNotificationResolution,
  getNotificationTone,
} from '../../utils/notificationDisplay';

const ACTION_CLASS =
  'rounded-md p-1.5 text-secondary-400 transition-colors hover:bg-secondary-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-secondary-600';

const NotificationItem = ({ notification, onOpen, onMarkRead, onDismiss }) => {
  const Icon = getNotificationIcon(notification.type);
  const unread = !notification.is_read;
  const resolution = getNotificationResolution(notification);

  return (
    <li className="group relative" data-testid={`notification-item-${notification.id}`}>
      <button
        type="button"
        onClick={() => onOpen(notification)}
        className={`flex w-full items-start gap-3 py-3 pl-5 text-left transition-colors hover:bg-secondary-50 focus:outline-none focus-visible:bg-secondary-50 dark:hover:bg-secondary-700/50 dark:focus-visible:bg-secondary-700/50 ${
          unread ? 'bg-primary-50/40 pr-16 dark:bg-primary-900/10' : 'pr-10'
        }`}
      >
        {unread && (
          <>
            <span
              className="absolute left-1.5 top-[1.375rem] h-2 w-2 rounded-full bg-primary-600"
              aria-hidden="true"
            />
            <span className="sr-only">Unread: </span>
          </>
        )}
        <span
          className={`mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${getNotificationTone(
            notification.type
          )}`}
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={`block truncate text-sm ${
              unread
                ? 'font-semibold text-secondary-900 dark:text-white'
                : 'font-medium text-secondary-700 dark:text-secondary-200'
            }`}
          >
            {notification.title}
          </span>
          <span className="mt-0.5 block line-clamp-2 text-xs text-secondary-500 dark:text-secondary-400">
            {notification.message}
          </span>
          <span className="mt-1.5 flex flex-wrap items-center gap-2">
            <time
              dateTime={notification.created_at}
              title={formatNotificationTimestamp(notification.created_at)}
              className="text-[11px] text-secondary-400"
            >
              {formatNotificationTime(notification.created_at)}
            </time>
            {resolution && (
              <StatusPill
                status={resolution.status}
                colorClass={resolution.colorClass}
                label={resolution.label}
              />
            )}
          </span>
        </span>
      </button>
      <div className="absolute right-2 top-2 flex items-center gap-0.5 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
        {unread && (
          <button
            type="button"
            onClick={() => onMarkRead(notification)}
            className={`${ACTION_CLASS} hover:text-primary-600`}
            aria-label="Mark as read"
            title="Mark as read"
          >
            <Check className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={() => onDismiss(notification)}
          className={`${ACTION_CLASS} hover:text-rose-600`}
          aria-label="Dismiss notification"
          title="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </li>
  );
};

export default NotificationItem;
